export enum SearchParameterFieldKind {
  ENUM = 'enum',
  ENUM_ARRAY = 'enum-array',
  STRING = 'string',
  STRING_ARRAY = 'string-array',
  NUMBER = 'number',
  AGREEMENTS = 'agreements',
}

type SchemaRowBase = {
  name: string;
};

/**
 * Whether the default survives into a serialized query.
 *
 * Defaults are dropped for SEO, so one search yields one url. A row opts out
 * when something downstream would read the absence as a *different* default
 * than ours, and silently answer a different search:
 *
 * - `assettype` — `SearchPathBuilder` reads a missing assettype as `image`
 *   (`queryParameters.assettype || 'image'`), not `image,film`, so dropping it
 *   turns every mixed search into an image-only one. It deletes the parameter
 *   from the query itself once it has picked the path segment, so preserving it
 *   costs nothing in the url.
 * - `sort` — the search service falls through to the visitor's stored sort when
 *   the query names none, so dropping a resolved `best` lets a remembered sort
 *   override it.
 *
 * Both are really "a value that exists only to defeat a downstream default",
 * which `URL_ONLY_SIGNALS` in the adapters already names and handles for
 * `istockcollection`, `editorialproducts` and `agreements`. These two sit here
 * instead because their stated value varies with the search, where that
 * mechanism takes a constant. Worth collapsing into one.
 *
 * Paired with `defaultValue` rather than sitting beside it, so a row cannot ask
 * for a default to be preserved without declaring one. A row that could say
 * `preserveDefault: true` and nothing else would be the same stale declaration
 * this replaces, just spelled differently.
 */
type DefaultOf<T> =
  | { defaultValue?: T; preserveDefault?: never }
  | { defaultValue: T; preserveDefault: true };

type SchemaEnumRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.ENUM;
  enum: Record<string, string>;
} & DefaultOf<string>;

// Number rows already require a `defaultValue`, so the flag needs no pairing.
type SchemaNumberRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.NUMBER;
  defaultValue: number;
  preserveDefault?: true;
};

type SchemaEnumArrayRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.ENUM_ARRAY;
  enum: Record<string, string>;
};

type SchemaStringRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.STRING;
  validate?: (value: string) => boolean;
};

type SchemaStringArrayRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.STRING_ARRAY;
};

type SchemaAgreementsRow = SchemaRowBase & {
  kind: SearchParameterFieldKind.AGREEMENTS;
};

export type SearchParameterSchemaRow =
  | SchemaEnumRow
  | SchemaEnumArrayRow
  | SchemaStringRow
  | SchemaStringArrayRow
  | SchemaNumberRow
  | SchemaAgreementsRow;

/**
 * Whether a parameter holds several values rather than one.
 *
 * Both kinds that do are enumerated here rather than at each call site, so
 * that adding a third array kind reaches everything that asks the question —
 * serialization, and the toggle/diff behavior on SearchParameters.
 */
export const isArrayKind = (kind: SearchParameterFieldKind): boolean =>
  kind === SearchParameterFieldKind.ENUM_ARRAY ||
  kind === SearchParameterFieldKind.STRING_ARRAY;
